import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { useAuth } from './contexts/AuthContext';
import { userProfileAPI } from './services/api';
import Home from './components/Home';
import Login from './components/Login';
import GoogleCallback from './components/GoogleCallback';
import MicrosoftCallback from './components/MicrosoftCallback';
import ProtectedRoute from './components/ProtectedRoute';
import CustomerList from './components/CustomerList';
import CustomerForm from './components/CustomerForm';
import PurchaseOrderList from './components/PurchaseOrderList';
import PurchaseOrderForm from './components/PurchaseOrderForm';
import PurchaseOrderDetail from './components/PurchaseOrderDetail';
import InvoiceList from './components/InvoiceList';
import InvoiceForm from './components/InvoiceForm';
import InvoiceDetail from './components/InvoiceDetail';
import ProjectList from './components/ProjectList';
import ProjectForm from './components/ProjectForm';
import BillingRateList from './components/BillingRateList';
import BillingRateForm from './components/BillingRateForm';
import UserRateManagement from './components/UserRateManagement';
import TimeEntry from './components/TimeEntry';
import RateGroupList from './components/RateGroupList';
import RateGroupForm from './components/RateGroupForm';
import TimesheetApprovalQueue from './components/TimesheetApprovalQueue';
import CustomerTimesheetReport from './components/CustomerTimesheetReport';
import UnbilledEntriesReport from './components/UnbilledEntriesReport';
import PayPeriodManagement from './components/PayPeriodManagement';
import BiweeklyTimesheet from './components/BiweeklyTimesheet';
import BiweeklyApprovalQueue from './components/BiweeklyApprovalQueue';
import TimeClock from './components/TimeClock';
import TimesheetHistory from './components/TimesheetHistory';
import AdminPunchDashboard from './components/AdminPunchDashboard';
import AdminPayRates from './components/AdminPayRates';
import PayrollReport from './components/PayrollReport';
import ChangePassword from './components/ChangePassword';
import AdminEmployees from './components/AdminEmployees';

const isTimeClockMode = process.env.REACT_APP_MODE === 'timeclock';

// Navigation component to handle dropdowns and mobile menu
const Navigation: React.FC<{
  isAdmin: boolean;
  isHourly: boolean;
  user: { username?: string; role?: string; has_usable_password?: boolean; must_change_password?: boolean } | null;
  logout: () => void;
}> = ({ isAdmin, isHourly, user, logout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setBillingOpen(false);
        setAdminOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu and dropdowns on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setBillingOpen(false);
    setAdminOpen(false);
  }, [location.pathname]);

  const toggleDropdown = (dropdown: 'billing' | 'admin') => {
    if (dropdown === 'billing') {
      setBillingOpen(!billingOpen);
      setAdminOpen(false);
    } else {
      setAdminOpen(!adminOpen);
      setBillingOpen(false);
    }
  };

  // Check if any billing route is active
  const isBillingActive = ['/customers', '/projects', '/purchase-orders', '/invoices', '/unbilled-time'].some(
    path => location.pathname.startsWith(path)
  );

  // Check if any admin route is active
  const isAdminActive = ['/billing-rates', '/rate-groups', '/user-rates', '/timesheets/approvals', '/timesheets/report', '/pay-periods', '/biweekly-approvals', '/admin-punches', '/admin-pay-rates', '/payroll-report', '/admin-employees', '/admin-reset-password'].some(
    path => location.pathname.startsWith(path)
  );

  const showBillingNav = isAdmin && !isTimeClockMode;
  const showBrsAdminItems = isAdmin && !isTimeClockMode;
  const showNclAdminItems = isAdmin && isTimeClockMode;
  const showTimeEntry = !isTimeClockMode;
  const showTimeClock = !isTimeClockMode;
  const showBiweekly = isHourly && !isTimeClockMode;
  const isTimeClockEmployee = isTimeClockMode && !isAdmin;
  const passwordChangeRequired = user?.must_change_password ?? false;

  return (
    <nav className="navbar" ref={navRef}>
      <div className="navbar-content">
        <div className="navbar-left">
          {!passwordChangeRequired ? (
            <NavLink to="/" className="navbar-brand" end>
              {process.env.REACT_APP_NAME || 'Invoicing App'}
            </NavLink>
          ) : (
            <h1>{process.env.REACT_APP_NAME || 'Invoicing App'}</h1>
          )}
          {!isTimeClockEmployee && !passwordChangeRequired && (
            <button
              className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          )}
        </div>

        <div className={`navbar-menu ${isTimeClockEmployee ? 'open' : (mobileMenuOpen ? 'open' : '')}`}>
          {!passwordChangeRequired && (
          <div className="navbar-links">
            {!isTimeClockEmployee && (
              <NavLink to="/" end>
                Home
              </NavLink>
            )}

            {isTimeClockEmployee && (
              <NavLink to="/" end>
                Time Clock
              </NavLink>
            )}

            {showBillingNav && (
              <div className="dropdown">
                <button
                  className={`dropdown-toggle ${isBillingActive ? 'active' : ''}`}
                  onClick={() => toggleDropdown('billing')}
                >
                  Billing <span className="dropdown-arrow">▼</span>
                </button>
                <div className={`dropdown-menu ${billingOpen ? 'open' : ''}`}>
                  <NavLink to="/customers">Companies</NavLink>
                  <NavLink to="/projects">Projects</NavLink>
                  <NavLink to="/purchase-orders">Purchase Orders</NavLink>
                  <NavLink to="/invoices">Invoices</NavLink>
                  <NavLink to="/unbilled-time">Unbilled Time</NavLink>
                </div>
              </div>
            )}

            {showBrsAdminItems && (
              <div className="dropdown">
                <button
                  className={`dropdown-toggle ${isAdminActive ? 'active' : ''}`}
                  onClick={() => toggleDropdown('admin')}
                >
                  Admin <span className="dropdown-arrow">▼</span>
                </button>
                <div className={`dropdown-menu ${adminOpen ? 'open' : ''}`}>
                  <NavLink to="/billing-rates">Rates</NavLink>
                  <NavLink to="/rate-groups">Rate Groups</NavLink>
                  <NavLink to="/user-rates">User Assignments</NavLink>
                  <NavLink to="/pay-periods">Pay Periods</NavLink>
                  <NavLink to="/timesheets/approvals">Approve Monthly</NavLink>
                  <NavLink to="/biweekly-approvals">Approve Biweekly</NavLink>
                  <NavLink to="/timesheets/report">Timesheet Report</NavLink>
                  <NavLink to="/admin-punches">Time &amp; Attendance</NavLink>
                  <NavLink to="/admin-pay-rates">Pay Rates</NavLink>
                  <NavLink to="/payroll-report">Payroll Report</NavLink>
                </div>
              </div>
            )}

            {showNclAdminItems && (
              <div className="dropdown">
                <button
                  className={`dropdown-toggle ${isAdminActive ? 'active' : ''}`}
                  onClick={() => toggleDropdown('admin')}
                >
                  Admin <span className="dropdown-arrow">▼</span>
                </button>
                <div className={`dropdown-menu ${adminOpen ? 'open' : ''}`}>
                  <NavLink to="/pay-periods">Pay Periods</NavLink>
                  <NavLink to="/admin-punches">Time &amp; Attendance</NavLink>
                  <NavLink to="/admin-pay-rates">Pay Rates</NavLink>
                  <NavLink to="/payroll-report">Payroll Report</NavLink>
                  <NavLink to="/admin-employees">Employees</NavLink>
                </div>
              </div>
            )}

            {showTimeEntry && <NavLink to="/time-entry">Time Entry</NavLink>}
            {showTimeClock && <NavLink to="/time-clock">Time Clock</NavLink>}
            {showBiweekly && <NavLink to="/biweekly">Biweekly</NavLink>}
          </div>
          )}

          <div className="navbar-right">
            {user?.has_usable_password && !passwordChangeRequired && (
              <NavLink to="/change-password" className="navbar-link" style={{ marginRight: '1rem' }}>
                Change Password
              </NavLink>
            )}
            <span className="navbar-user">
              {user?.username} ({user?.role?.replace('_', ' ')})
            </span>
            <button onClick={logout} className="btn btn-secondary navbar-logout">
              Logout
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

const App: React.FC = () => {
  const { isAuthenticated, user, logout, isAdmin } = useAuth();
  const [isHourly, setIsHourly] = useState(false);

  // Fetch user profile to check if they're hourly
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!isAuthenticated || !user?.id) {
        setIsHourly(false);
        return;
      }
      try {
        // Use the /me/ endpoint which is accessible to all authenticated users
        const response = await userProfileAPI.getMyProfile();
        setIsHourly(response.data.employee_type === 'hourly');
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
        setIsHourly(false);
      }
    };
    fetchUserProfile();
  }, [isAuthenticated, user?.id]);

  return (
    <Router>
      <div className="App">
        {isAuthenticated && (
          <Navigation isAdmin={isAdmin} isHourly={isHourly} user={user} logout={logout} />
        )}

        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                {isTimeClockMode && !isAdmin ? <TimeClock /> : <Home />}
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute requireAdmin>
                <CustomerList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/new"
            element={
              <ProtectedRoute requireAdmin>
                <CustomerForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/:id/edit"
            element={
              <ProtectedRoute requireAdmin>
                <CustomerForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders"
            element={
              <ProtectedRoute requireAdmin>
                <PurchaseOrderList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders/new"
            element={
              <ProtectedRoute requireAdmin>
                <PurchaseOrderForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders/:id"
            element={
              <ProtectedRoute requireAdmin>
                <PurchaseOrderDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders/:id/edit"
            element={
              <ProtectedRoute requireAdmin>
                <PurchaseOrderForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute requireAdmin>
                <InvoiceList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/new"
            element={
              <ProtectedRoute requireAdmin>
                <InvoiceForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ProtectedRoute requireAdmin>
                <InvoiceDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id/edit"
            element={
              <ProtectedRoute requireAdmin>
                <InvoiceForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects"
            element={
              <ProtectedRoute requireAdmin>
                <ProjectList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/new"
            element={
              <ProtectedRoute requireAdmin>
                <ProjectForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id/edit"
            element={
              <ProtectedRoute requireAdmin>
                <ProjectForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/billing-rates"
            element={
              <ProtectedRoute requireAdmin>
                <BillingRateList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing-rates/new"
            element={
              <ProtectedRoute requireAdmin>
                <BillingRateForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing-rates/:id/edit"
            element={
              <ProtectedRoute requireAdmin>
                <BillingRateForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/user-rates"
            element={
              <ProtectedRoute requireAdmin>
                <UserRateManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/rate-groups"
            element={
              <ProtectedRoute requireAdmin>
                <RateGroupList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-groups/new"
            element={
              <ProtectedRoute requireAdmin>
                <RateGroupForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-groups/:id/edit"
            element={
              <ProtectedRoute requireAdmin>
                <RateGroupForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/time-entry"
            element={
              <ProtectedRoute>
                <TimeEntry />
              </ProtectedRoute>
            }
          />

          <Route
            path="/timesheets/approvals"
            element={
              <ProtectedRoute requireAdmin>
                <TimesheetApprovalQueue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/timesheets/report"
            element={
              <ProtectedRoute requireAdmin>
                <CustomerTimesheetReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/unbilled-time"
            element={
              <ProtectedRoute requireAdmin>
                <UnbilledEntriesReport />
              </ProtectedRoute>
            }
          />

          {/* NCL Admin Routes */}
          <Route
            path="/admin-punches"
            element={
              <ProtectedRoute requireAdmin>
                <AdminPunchDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-pay-rates"
            element={
              <ProtectedRoute requireAdmin>
                <AdminPayRates />
              </ProtectedRoute>
            }
          />

          <Route
            path="/payroll-report"
            element={
              <ProtectedRoute requireAdmin>
                <PayrollReport />
              </ProtectedRoute>
            }
          />

          <Route
            path="/change-password"
            element={
              <ProtectedRoute allowPasswordChange>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-employees"
            element={
              <ProtectedRoute requireAdmin>
                <AdminEmployees />
              </ProtectedRoute>
            }
          />
          <Route path="/admin-reset-password" element={<Navigate to="/admin-employees" replace />} />

          {/* NCL Time Clock Routes */}
          <Route
            path="/time-clock"
            element={
              <ProtectedRoute>
                <TimeClock />
              </ProtectedRoute>
            }
          />
          <Route
            path="/timesheet-history"
            element={
              <ProtectedRoute>
                <TimesheetHistory />
              </ProtectedRoute>
            }
          />

          {/* Biweekly Payroll Routes */}
          <Route
            path="/pay-periods"
            element={
              <ProtectedRoute requireAdmin>
                <PayPeriodManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/biweekly"
            element={
              <ProtectedRoute>
                <BiweeklyTimesheet />
              </ProtectedRoute>
            }
          />
          <Route
            path="/biweekly-approvals"
            element={
              <ProtectedRoute requireAdmin>
                <BiweeklyApprovalQueue />
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
